import React, { FC, useMemo, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslate } from 'react-admin';

import { AmountField, StatusField, TextField } from '~/components';
import { Section } from '~/layout';
import { Longdash } from '~/utils';
import { Batch, BatchRecord } from '~/types/Batch';
import { buildProfileLink } from '~/utils/identities';

export const l = (key: string): string => `resources.batches.show.records.${key}`;

type SortField = 'index' | 'beneficiary' | 'amount';
type SortDirection = 'asc' | 'desc';

interface Props {
  record?: Batch;
}

type NumberedRecord = { record: BatchRecord; number: number };

const compare: Record<SortField, (a: NumberedRecord, b: NumberedRecord) => number> = {
  index: (a, b) => a.number - b.number,
  beneficiary: (a, b) =>
    (a.record.profileDto?.name ?? '').localeCompare(b.record.profileDto?.name ?? ''),
  amount: (a, b) => (a.record.amount?.value ?? 0) - (b.record.amount?.value ?? 0),
};

const BatchRecordsTable: FC<Props> = ({ record: batch }) => {
  const classes = useStyles();
  const translate = useTranslate();
  const t = (key: string): string => translate(l(key));

  const batchRecords = batch?.data?.records ?? [];

  const [sortField, setSortField] = useState<SortField>('index');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const sorted = useMemo(() => {
    const numbered: NumberedRecord[] = batchRecords.map((record, index) => ({
      record,
      number: index + 1,
    }));
    const sign = sortDirection === 'asc' ? 1 : -1;

    return numbered.sort((a, b) => compare[sortField](a, b) * sign);
  }, [batchRecords, sortField, sortDirection]);

  const onSort = (field: SortField) => () => {
    if (field === sortField) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortableHeader = (field: SortField, label: string) => (
    <TableSortLabel
      active={sortField === field}
      direction={sortField === field ? sortDirection : 'asc'}
      onClick={onSort(field)}
    >
      {label}
    </TableSortLabel>
  );

  if (!batchRecords?.length) {
    return <Section>{t('empty')}</Section>;
  }

  return (
    <Section noPaddings>
      <Table className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell className={classes.numberCell}>
              {sortableHeader('index', t('number'))}
            </TableCell>
            <TableCell>{t('accountNumber')}</TableCell>
            <TableCell>{sortableHeader('beneficiary', t('beneficiary'))}</TableCell>
            <TableCell className={classes.amountCell}>
              {sortableHeader('amount', t('amount'))}
            </TableCell>
            <TableCell>{t('paymentDetails')}</TableCell>
            <TableCell className={classes.statusCell}>{t('processingStatus')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map(({ record, number }) => (
            <TableRow key={record.id ?? number}>
              <TableCell className={classes.numberCell}>{number}</TableCell>
              <TableCell className={classes.cell}>
                <TextField
                  showLink={
                    !!record?.accountNumber && buildProfileLink('ACCOUNT', record?.accountId)
                  }
                  copyable={record?.accountNumber}
                >
                  <span className={classes.ellipsis}>{record?.accountNumber || Longdash}</span>
                </TextField>
              </TableCell>
              <TableCell className={classes.cell}>
                <TextField
                  showLink={
                    !!record?.profileDto?.name &&
                    buildProfileLink(record?.profileDto?.profileType, record?.profileDto?.profileId)
                  }
                  copyable={record?.profileDto?.name}
                >
                  <span className={classes.ellipsis}>{record?.profileDto?.name || Longdash}</span>
                </TextField>
              </TableCell>
              <TableCell>
                <AmountField amount={record.amount} />
              </TableCell>
              <TableCell className={classes.cell}>{record.paymentDetails || Longdash}</TableCell>
              <TableCell>
                <StatusField status={record.processingStatus} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Section>
  );
};

const useStyles = makeStyles(() => ({
  table: {
    width: '100%',
    tableLayout: 'fixed',
  },
  cell: {
    overflow: 'hidden',
  },
  ellipsis: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  numberCell: {
    width: 20,
  },
  amountCell: {
    width: 100,
  },
  statusCell: {
    width: 120,
  },
}));

export default BatchRecordsTable;
